import { Children, isValidElement, type ReactElement } from 'react'

import type { ChildrenProps } from '@subaio/components'

const getChildElements = (element: ReactElement) => {
	const { children, ...props } = element.props as ChildrenProps

	return [...Object.values(props), ...Children.toArray(children)].filter(isValidElement)
}

/** Search through a finished element tree for a match. Looks through children and all element props. */
export const findElement = <T extends ReactElement>(
	element: ReactElement,
	isMatch: (element: ReactElement) => boolean,
): T | undefined => {
	if (isMatch(element)) return element as T

	for (const child of getChildElements(element)) {
		const match = findElement(child, isMatch)

		if (match) return match as T
	}

	return undefined
}

/** Search through a finished element tree for a match. Looks through children and all element props. */
export const findElements = <T extends ReactElement>(
	element: ReactElement,
	isMatch: (element: ReactElement) => boolean,
): Array<T> => {
	if (isMatch(element)) return [element as T]

	return getChildElements(element).flatMap((child) => findElements(child, isMatch))
}
